// Адрес анкеты на карте «Рядом с домом»: /tutors/<slug>.
//
// slug = латиница из имени + «-» + первые 6 hex-знаков id анкеты (uuid).
// Хвост из id не меняется при переименовании, поэтому старый адрес находит
// анкету по хвосту и отвечает 301 на её текущий slug (resolveMovedSlug ниже,
// вызов — getPublicPartner и loader маршрутов /tutors/$slug).
//
// Без серверных импортов: модуль читают и сервер, и scripts/verify-tutor-slug-markdown.mts.

/**
 * Азербайджанские и русские буквы → латиница. Заглавные перечислены сами:
 * транслит идёт ДО toLowerCase, потому что "İ".toLowerCase() даёт «i» +
 * U+0307 (комбинирующая точка), и та превращалась в «-»:
 * «İsgəndərova» → «i-sgenderova».
 */
// prettier-ignore
const TRANSLIT: Record<string, string> = {
  Ə: "e", ə: "e", İ: "i", I: "i", ı: "i", Ö: "o", ö: "o", Ü: "u", ü: "u",
  Ğ: "g", ğ: "g", Ş: "s", ş: "s", Ç: "c", ç: "c",
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m",
  н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "kh", ц: "ts", ч: "ch", ш: "sh", щ: "sch",
  ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};

const translit = (s: string) => [...s].map((ch) => TRANSLIT[ch] ?? ch).join("");

/** Латиница для адреса карточки: «Nuranə İsgəndərova» → «nurane-isgenderova-<6 знаков id>». */
export function slugify(name: string, id: string): string {
  const base = translit(translit(name).toLowerCase())
    .normalize("NFKD")
    // Комбинирующие знаки (U+0300–U+036F) после NFKD — убрать, а не заменить
    // на «-»: «é» → «e», а не «e-».
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48)
    .replace(/-+$/, "");
  return `${base || "partner"}-${id.slice(0, 6).toLowerCase()}`;
}

/** Хвост slug из id: последние 6 hex-знаков после «-». Нет такого хвоста — null. */
export function slugSuffix(slug: string): string | null {
  const m = /(?:^|-)([0-9a-f]{6})$/.exec(slug.toLowerCase());
  return m ? m[1] : null;
}

/**
 * Границы uuid, начинающихся на хвост: id между lo и hi включительно.
 * PostgREST не умеет like по uuid, а сравнение uuid работает.
 */
export function idRangeForSuffix(suffix: string): { lo: string; hi: string } {
  return {
    lo: `${suffix}00-0000-0000-0000-000000000000`,
    hi: `${suffix}ff-ffff-ffff-ffff-ffffffffffff`,
  };
}

/**
 * Запрошенного slug нет. Кандидаты — опубликованные анкеты, чей id начинается
 * на хвост запрошенного slug. Ровно один кандидат с другим slug — вернуть его
 * slug (на него 301). Ноль или больше одного (хвост неоднозначен) — null, 404.
 */
export function resolveMovedSlug(
  requested: string,
  candidates: ReadonlyArray<{ id: string; slug: string }>,
): string | null {
  const suffix = slugSuffix(requested);
  if (!suffix) return null;
  const hits = candidates.filter((c) => c.id.toLowerCase().startsWith(suffix));
  if (hits.length !== 1) return null;
  const to = hits[0].slug;
  return to && to !== requested ? to : null;
}

/** Латинский slug из любого текста (AZ/RU/EN) — без хвоста id. Блог: «Как выбрать вуз» → «kak-vybrat-vuz». */
export function latinSlug(text: string, max = 80): string {
  return translit(translit(text).toLowerCase())
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, max)
    .replace(/-+$/, "");
}
